import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, ArrowRight, ArrowLeft, ClipboardText, Lock } from '@phosphor-icons/react';
import { track } from '@/lib/analytics';

const MODULE_OPTIONS = [
  { value: 'dashboard', label: 'Executive Dashboard' },
  { value: 'income_sources', label: 'Income Sources' },
  { value: 'spending_tracker', label: 'Spending / Expenses' },
  { value: 'net_worth', label: 'Net Worth' },
  { value: 'wealth_health_score', label: 'Wealth Health Score™' },
  { value: 'retirement_gap_analyzer', label: 'Retirement Gap Analyzer' },
  { value: 'scenario_planner', label: 'Scenario Planner' },
  { value: 'financial_journey_timeline', label: 'Journey Timeline' },
  { value: 'social_security', label: 'Social Security Optimizer' },
  { value: 'roth_conversion', label: 'Roth Conversion Analyzer' },
  { value: 'rmd_planning', label: 'RMD Planner' },
  { value: 'tax_optimizer', label: 'Tax-Aware Withdrawal Optimizer' },
  { value: 'retirement_planner', label: 'Retirement Income Planner' },
  { value: 'ai_coach', label: 'AI Financial Coach' },
  { value: 'reports', label: 'Reports & PDF Exports' },
];

const initial = {
  ease_navigation: 0,
  ease_next_step: 0,
  needed_assistance: '',
  assistance_detail: '',
  clarity_change: '',
  identified_issue: '',
  issue_detail: '',
  changed_thinking: '',
  thinking_detail: '',
  most_useful_features: [],
  single_most_valuable: '',
  confusing_feature: '',
  missing_feature: '',
  value_rating: 0,
  reuse_likelihood: 0,
  nps: -1,
  trusted_channel_value: '',
  preferred_access: '',
  preferred_access_other: '',
  willingness_to_pay: '',
  worth_paying_detail: '',
  liked_most: '',
  one_improvement: '',
  anything_else: '',
  follow_up_ok: false,
  anonymized_use_ok: false,
};

const Scale = ({ value, onChange, lo = 1, hi = 5, loLabel, hiLabel, testid }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
    <span className="text-xs text-[#A0AABF] w-24">{loLabel}</span>
    {Array.from({ length: hi - lo + 1 }, (_, i) => lo + i).map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        data-testid={`${testid}-${n}`}
        className={`w-10 h-10 rounded-md border font-semibold text-sm transition ${
          value === n ? 'bg-[#0A1128] text-[#C5A059] border-[#0A1128]' : 'bg-white text-[#4A5568] border-slate-300 hover:border-[#C5A059]'
        }`}
      >
        {n}
      </button>
    ))}
    <span className="text-xs text-[#A0AABF]">{hiLabel}</span>
  </div>
);

const Radio = ({ value, onChange, options, name }) => (
  <div className="grid gap-2 mt-2">
    {options.map((o) => (
      <label key={o.value} className="flex items-start gap-2 cursor-pointer text-sm text-[#1A202C]" data-testid={`${name}-${o.value}`}>
        <input
          type="radio"
          name={name}
          value={o.value}
          checked={value === o.value}
          onChange={() => onChange(o.value)}
          className="mt-1 accent-[#0A1128]"
        />
        <span>{o.label}</span>
      </label>
    ))}
  </div>
);

const BetaEvaluation = () => {
  const [status, setStatus] = useState(null); // beta-program/status response
  const [answers, setAnswers] = useState(initial);
  const [submitted, setSubmitted] = useState(null); // submission response
  const [section, setSection] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { track('beta_evaluation_started', { source: 'page_view' }); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, mine] = await Promise.all([
          api.get('/beta-program/status'),
          api.get('/beta-program/evaluation/mine'),
        ]);
        if (cancelled) return;
        setStatus(s.data);
        if (mine.data?.evaluation) setSubmitted(mine.data.evaluation);
      } catch (_e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const setField = (k, v) => setAnswers((prev) => ({ ...prev, [k]: v }));
  const toggleFeature = (v) =>
    setAnswers((prev) => ({
      ...prev,
      most_useful_features: prev.most_useful_features.includes(v)
        ? prev.most_useful_features.filter((x) => x !== v)
        : [...prev.most_useful_features, v],
    }));

  const eligible = !!status?.eligible_for_evaluation;
  const alreadySubmitted = !!submitted?.submitted_at;

  const canSubmit = useMemo(() => {
    return (
      answers.ease_navigation && answers.ease_next_step &&
      answers.needed_assistance && answers.clarity_change &&
      answers.identified_issue && answers.changed_thinking &&
      answers.value_rating && answers.reuse_likelihood &&
      answers.nps >= 0 && answers.trusted_channel_value &&
      answers.preferred_access && answers.willingness_to_pay
    );
  }, [answers]);

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Please answer every required question before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/beta-program/evaluation', answers);
      track('beta_evaluation_completed', { is_followup: data.is_followup });
      // reload mine
      const mine = await api.get('/beta-program/evaluation/mine');
      setSubmitted(mine.data?.evaluation);
      toast.success('Thank you — your feedback has been submitted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- View: not eligible yet ----
  if (status && !eligible && !alreadySubmitted) {
    const remaining = status.min_required - status.qualifying_count;
    const pct = Math.round((status.qualifying_count / status.min_required) * 100);
    return (
      <div className="max-w-3xl mx-auto" data-testid="beta-eval-progress-view">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-semibold">Private Beta</p>
          <h1 className="cabinet-grotesk text-3xl font-medium text-[#0A1128] mt-1">Beta Evaluation</h1>
        </div>
        <div className="bg-white rounded-md border border-slate-200 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-5">
            <Lock size={24} weight="duotone" className="text-[#C5A059] mt-1 flex-shrink-0" />
            <div>
              <h2 className="cabinet-grotesk text-lg font-semibold text-[#0A1128]">
                You're almost ready to share your feedback
              </h2>
              <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">
                Please explore at least <strong>{status.min_required} PLANIVANCE AI modules</strong> before completing your beta evaluation so your responses reflect the full platform experience.
              </p>
            </div>
          </div>
          <div className="mb-2 flex items-center justify-between text-xs text-[#4A5568]">
            <span>{status.qualifying_count} of {status.min_required} modules explored</span>
            <span className="font-semibold text-[#0A1128]">{pct}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-[#A0AABF] mt-4">
            {remaining > 0 ? `${remaining} more to unlock the evaluation.` : 'Refresh to unlock the evaluation.'}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => navigate('/')}
              data-testid="beta-eval-explore-button"
              className="bg-[#0A1128] hover:bg-[#152145] w-full sm:w-auto"
            >
              Continue exploring PLANIVANCE AI <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- View: already submitted ----
  if (alreadySubmitted) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="beta-eval-submitted-view">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-semibold">Private Beta</p>
          <h1 className="cabinet-grotesk text-3xl font-medium text-[#0A1128] mt-1">Beta Evaluation</h1>
        </div>
        <div className="bg-white rounded-md border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={28} weight="fill" className="text-[#2D6A4F]" />
          </div>
          <h2 className="cabinet-grotesk text-xl font-semibold text-[#0A1128]">Thank You</h2>
          <p className="text-sm text-[#4A5568] mt-2 max-w-lg mx-auto">
            Your feedback has been submitted successfully. Thank you for helping us improve PLANIVANCE AI.
          </p>
          <p className="text-xs text-[#A0AABF] mt-4">
            Submitted {new Date(submitted.submitted_at).toLocaleString()}
          </p>
        </div>
      </div>
    );
  }

  if (!status) return <div className="p-6 text-sm text-[#A0AABF]">Loading…</div>;

  // ---- View: full evaluation ----
  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="beta-eval-form-view">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-semibold">Private Beta</p>
        <h1 className="cabinet-grotesk text-3xl font-medium text-[#0A1128] mt-1">Help Shape PLANIVANCE AI</h1>
        <p className="text-sm text-[#4A5568] mt-2 leading-relaxed max-w-2xl">
          Thank you for participating in the PLANIVANCE AI Private Beta. Your candid feedback will help us
          understand what's working, what's unclear, and what would make PLANIVANCE more valuable.
          Approximately 5–7 minutes.
        </p>
      </div>

      <div className="bg-white rounded-md border border-slate-200 shadow-sm p-6 space-y-10">

        {/* Section 1 */}
        <section data-testid="section-1">
          <h2 className="cabinet-grotesk text-lg font-semibold text-[#0A1128] mb-4 flex items-center gap-2">
            <ClipboardText size={18} weight="duotone" className="text-[#C5A059]" />
            Section 1 — User Experience
          </h2>
          <div className="space-y-6">
            <div>
              <Label className="text-sm text-[#1A202C]">How easy was PLANIVANCE AI to navigate?</Label>
              <Scale value={answers.ease_navigation} onChange={(v) => setField('ease_navigation', v)}
                     loLabel="Very Difficult" hiLabel="Very Easy" testid="ease-navigation" />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">How easy was it to understand what to do next?</Label>
              <Scale value={answers.ease_next_step} onChange={(v) => setField('ease_next_step', v)}
                     loLabel="Very Difficult" hiLabel="Very Easy" testid="ease-next-step" />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Did you need assistance while using PLANIVANCE?</Label>
              <Radio value={answers.needed_assistance} onChange={(v) => setField('needed_assistance', v)}
                     name="needed-assistance"
                     options={[{ value: 'no', label: 'No' }, { value: 'once', label: 'Yes, once' }, { value: 'more_than_once', label: 'Yes, more than once' }]} />
              {answers.needed_assistance && answers.needed_assistance !== 'no' && (
                <Textarea data-testid="assistance-detail" className="mt-3 border-slate-300"
                          placeholder="What did you need help with?"
                          value={answers.assistance_detail}
                          onChange={(e) => setField('assistance_detail', e.target.value)} rows={2} />
              )}
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section data-testid="section-2">
          <h2 className="cabinet-grotesk text-lg font-semibold text-[#0A1128] mb-4">Section 2 — Financial Understanding</h2>
          <div className="space-y-6">
            <div>
              <Label className="text-sm text-[#1A202C]">Do you feel you have a clearer understanding of your overall financial and retirement picture?</Label>
              <Radio value={answers.clarity_change} onChange={(v) => setField('clarity_change', v)}
                     name="clarity-change"
                     options={[
                       { value: 'much_clearer', label: 'Much clearer' },
                       { value: 'somewhat_clearer', label: 'Somewhat clearer' },
                       { value: 'same', label: 'About the same' },
                       { value: 'less_clear', label: 'Less clear' },
                     ]} />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Did PLANIVANCE help you identify a financial or retirement issue you would like to explore further?</Label>
              <Radio value={answers.identified_issue} onChange={(v) => setField('identified_issue', v)}
                     name="identified-issue"
                     options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not sure' }]} />
              {answers.identified_issue === 'yes' && (
                <Textarea data-testid="issue-detail" className="mt-3 border-slate-300"
                          placeholder="Please describe it."
                          value={answers.issue_detail}
                          onChange={(e) => setField('issue_detail', e.target.value)} rows={2} />
              )}
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Did using PLANIVANCE lead you to think differently about any financial or retirement decision?</Label>
              <Radio value={answers.changed_thinking} onChange={(v) => setField('changed_thinking', v)}
                     name="changed-thinking"
                     options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not sure' }]} />
              {answers.changed_thinking === 'yes' && (
                <Textarea data-testid="thinking-detail" className="mt-3 border-slate-300"
                          placeholder="Please explain."
                          value={answers.thinking_detail}
                          onChange={(e) => setField('thinking_detail', e.target.value)} rows={2} />
              )}
            </div>
          </div>
        </section>

        {/* Section 3 */}
        <section data-testid="section-3">
          <h2 className="cabinet-grotesk text-lg font-semibold text-[#0A1128] mb-4">Section 3 — Feature Value</h2>
          <div className="space-y-6">
            <div>
              <Label className="text-sm text-[#1A202C]">Which PLANIVANCE features did you find most useful? (select all)</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {MODULE_OPTIONS.map((m) => {
                  const active = answers.most_useful_features.includes(m.value);
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => toggleFeature(m.value)}
                      data-testid={`useful-${m.value}`}
                      className={`text-left text-xs px-3 py-2 rounded border transition ${
                        active
                          ? 'bg-[#0A1128] text-[#C5A059] border-[#0A1128] font-semibold'
                          : 'bg-white text-[#4A5568] border-slate-200 hover:border-[#C5A059]'
                      }`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Which feature was the single most valuable to you, and why?</Label>
              <Textarea data-testid="single-most-valuable" className="mt-2 border-slate-300"
                        value={answers.single_most_valuable}
                        onChange={(e) => setField('single_most_valuable', e.target.value)} rows={2} />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Was there any feature you found confusing or difficult to use?</Label>
              <Textarea data-testid="confusing-feature" className="mt-2 border-slate-300"
                        value={answers.confusing_feature}
                        onChange={(e) => setField('confusing_feature', e.target.value)} rows={2} />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Was there something you expected PLANIVANCE to do that you could not find?</Label>
              <Textarea data-testid="missing-feature" className="mt-2 border-slate-300"
                        value={answers.missing_feature}
                        onChange={(e) => setField('missing_feature', e.target.value)} rows={2} />
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section data-testid="section-4">
          <h2 className="cabinet-grotesk text-lg font-semibold text-[#0A1128] mb-4">Section 4 — Overall Value</h2>
          <div className="space-y-6">
            <div>
              <Label className="text-sm text-[#1A202C]">How valuable would a tool like PLANIVANCE be as you prepare for retirement or make long-term financial decisions?</Label>
              <Scale value={answers.value_rating} onChange={(v) => setField('value_rating', v)}
                     loLabel="Not Valuable" hiLabel="Extremely Valuable" testid="value-rating" />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">How likely would you be to use PLANIVANCE again?</Label>
              <Scale value={answers.reuse_likelihood} onChange={(v) => setField('reuse_likelihood', v)}
                     loLabel="Very Unlikely" hiLabel="Very Likely" testid="reuse-likelihood" />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">How likely would you be to recommend PLANIVANCE to someone preparing for retirement? (0–10)</Label>
              <div className="flex gap-1 flex-wrap mt-2" data-testid="nps">
                {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setField('nps', n)}
                    data-testid={`nps-${n}`}
                    className={`w-9 h-9 rounded-md border font-semibold text-sm transition ${
                      answers.nps === n ? 'bg-[#0A1128] text-[#C5A059] border-[#0A1128]' : 'bg-white text-[#4A5568] border-slate-300 hover:border-[#C5A059]'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Would you find PLANIVANCE valuable if it were offered through a financial professional, employer, retirement educator, or other trusted organization?</Label>
              <Radio value={answers.trusted_channel_value} onChange={(v) => setField('trusted_channel_value', v)}
                     name="trusted-channel"
                     options={[{ value: 'yes', label: 'Yes' }, { value: 'maybe', label: 'Maybe' }, { value: 'no', label: 'No' }]} />
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section data-testid="section-5">
          <h2 className="cabinet-grotesk text-lg font-semibold text-[#0A1128] mb-4">Section 5 — Commercial Validation</h2>
          <div className="space-y-6">
            <div>
              <Label className="text-sm text-[#1A202C]">If PLANIVANCE were available after the beta, which access method would you prefer?</Label>
              <Radio value={answers.preferred_access} onChange={(v) => setField('preferred_access', v)}
                     name="preferred-access"
                     options={[
                       { value: 'advisor', label: 'Through my financial professional' },
                       { value: 'employer', label: 'Through my employer' },
                       { value: 'education', label: 'Through a retirement/financial education program' },
                       { value: 'direct', label: 'Direct individual subscription' },
                       { value: 'other', label: 'Other' },
                     ]} />
              {answers.preferred_access === 'other' && (
                <Input data-testid="preferred-access-other" className="mt-3 border-slate-300"
                       placeholder="Please describe."
                       value={answers.preferred_access_other}
                       onChange={(e) => setField('preferred_access_other', e.target.value)} />
              )}
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Would you consider paying for access to PLANIVANCE AI?</Label>
              <Radio value={answers.willingness_to_pay} onChange={(v) => setField('willingness_to_pay', v)}
                     name="willingness"
                     options={[{ value: 'yes', label: 'Yes' }, { value: 'maybe', label: 'Maybe' }, { value: 'no', label: 'No' }]} />
              {(answers.willingness_to_pay === 'yes' || answers.willingness_to_pay === 'maybe') && (
                <Textarea data-testid="worth-paying" className="mt-3 border-slate-300"
                          placeholder="What would make PLANIVANCE worth paying for?"
                          value={answers.worth_paying_detail}
                          onChange={(e) => setField('worth_paying_detail', e.target.value)} rows={2} />
              )}
            </div>
          </div>
        </section>

        {/* Section 6 */}
        <section data-testid="section-6">
          <h2 className="cabinet-grotesk text-lg font-semibold text-[#0A1128] mb-4">Section 6 — Final Feedback</h2>
          <div className="space-y-6">
            <div>
              <Label className="text-sm text-[#1A202C]">What did you like most about PLANIVANCE?</Label>
              <Textarea data-testid="liked-most" className="mt-2 border-slate-300"
                        value={answers.liked_most}
                        onChange={(e) => setField('liked_most', e.target.value)} rows={2} />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">What is the ONE thing you would change or improve?</Label>
              <Textarea data-testid="one-improvement" className="mt-2 border-slate-300"
                        value={answers.one_improvement}
                        onChange={(e) => setField('one_improvement', e.target.value)} rows={2} />
            </div>
            <div>
              <Label className="text-sm text-[#1A202C]">Is there anything else you would like us to know?</Label>
              <Textarea data-testid="anything-else" className="mt-2 border-slate-300"
                        value={answers.anything_else}
                        onChange={(e) => setField('anything_else', e.target.value)} rows={2} />
            </div>
            <div className="space-y-2 pt-2">
              <label className="flex items-start gap-2 text-sm text-[#1A202C]" data-testid="consent-follow-up">
                <input type="checkbox" className="mt-1 accent-[#0A1128]"
                       checked={answers.follow_up_ok}
                       onChange={(e) => setField('follow_up_ok', e.target.checked)} />
                <span>May we contact you for a brief follow-up conversation about your feedback?</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-[#1A202C]" data-testid="consent-anonymized">
                <input type="checkbox" className="mt-1 accent-[#0A1128]"
                       checked={answers.anonymized_use_ok}
                       onChange={(e) => setField('anonymized_use_ok', e.target.checked)} />
                <span>May we use an anonymized version of your feedback in PLANIVANCE AI research, product-development, investor, or partnership materials? (We will not publicly identify you without separate explicit permission.)</span>
              </label>
            </div>
          </div>
        </section>

        <div className="border-t border-slate-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#A0AABF]">
            You cannot edit your answers after submission.
          </p>
          <Button
            onClick={submit}
            disabled={submitting || !canSubmit}
            data-testid="beta-eval-submit-button"
            className="bg-[#0A1128] hover:bg-[#152145] w-full sm:w-auto"
          >
            {submitting ? 'Submitting…' : 'Submit Evaluation'} <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BetaEvaluation;
